import { AppLogo, AppLogoSmall } from 'components/_common/Icons';
import ExpandSvg from 'components/_common/Icons/SVGIcons/ExpandSvg';
import MenuCloseSvg from 'components/_common/Icons/SVGIcons/MenuCloseSvg';
import useWindowSize from 'hooks/useWindowSize';
import Topbar from 'modules/admin/components/Header/Topbar';
import Sidebar from 'modules/admin/components/Navbars/Sidebar';
import { useSidebarOrder } from 'modules/admin/components/Navbars/sidebarReOrder';
import ProfileCompletionAlert from 'modules/admin/components/ProfileCompletionAlert/ProfileCompletionAlert';
import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, matchPath, Outlet, useLocation } from 'react-router-dom';
import { adminMainRoutes } from 'routes/admin/AdminRoutes';
import ProtectedAdminRoute from 'routes/admin/ProtectedAdminRoute';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import { getDynamicModules } from 'store/admin/actions/AdminDynamicModuleAction';
import { type BadgeModule } from 'store/admin/actions/AdminModuleBadgeAction';
import {
  fetchBadgeCounts,
  fetchNotifications,
  markModuleViewedAndRefresh,
  setIsMobile,
} from 'store/admin/slices/AdminGlobalSlice';
import { useAppDispatch } from 'store/hooks';

import 'modules/admin/assets/scss/adminDataTable.scss';
import 'modules/admin/assets/scss/adminCustom.scss';

// The list pages that have a sidebar badge, and the badge each one clears when it is opened
const BADGE_LIST_PAGES: { path: string; module: BadgeModule }[] = [
  { path: adminMainRoutes.orderList.path, module: 'orders' },
  { path: adminMainRoutes.transferRequestList.path, module: 'transfer_requests' },
  { path: adminMainRoutes.returnRequestList.path, module: 'return_requests' },
  { path: adminMainRoutes.transactionList.path, module: 'transactions' },
];

export default function AdminMainLayout() {
  const [collasped, setCollasped] = useState<boolean>(true);
  const [isMobileView, setIsMobileView] = useState<boolean>(true);
  const [openMobileMenu, setOpenMobileMenu] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { width } = useWindowSize();

  // Every page change refreshes the sidebar badges. Opening one of the badge lists marks it seen,
  // so its badge starts again from now - that call answers with fresh counts for every list, so a
  // plain refresh isn't sent alongside it (it could land last and bring the old count back).
  const { pathname } = useLocation();
  useEffect(() => {
    const listPage = BADGE_LIST_PAGES.find((page) => matchPath(page.path, pathname));
    if (listPage) {
      dispatch(markModuleViewedAndRefresh(listPage.module));
    } else {
      dispatch(fetchBadgeCounts());
    }
  }, [dispatch, pathname]);

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(getDynamicModules() as any);
  }, [dispatch]);

  useEffect(() => {
    if (width === null) return;
    (() => {
      if (width >= 768 && width <= 1200) {
        const shouldCollapse = !(width >= 768 && width <= 1200);
        setCollasped((prev) => (prev === shouldCollapse ? prev : shouldCollapse));
      } else {
        setCollasped(true);
      }

      const isMobile = width < 768;
      setIsMobileView((prev) => (prev === isMobile ? prev : isMobile));
      dispatch(setIsMobile(width < 992));
    })();
  }, [width, dispatch]);

  const { orderedMenu } = useSidebarOrder();

  const toggleSidebar = (): void => {
    setCollasped((prev) => !prev);
  };

  return (
    <ProtectedAdminRoute>
      <div className={`d-flex admin-layout dark-layout ${isMobileView ? 'mobile-view' : ''}`}>
        <Sidebar
          collasped={collasped}
          openMobileMenu={openMobileMenu}
          setOpenMobileMenu={setOpenMobileMenu}
          menu={orderedMenu}
          maxWidth={isMobileView ? '70%' : 240}
          minWidth={64}
          Header={() => (
            <>
              <div className="flex-center px-2 d-none d-md-flex sidebar-brand">
                <Link to={customerMainRoutes.customerHome.path}>
                  <img
                    src={collasped ? AppLogo : AppLogoSmall}
                    alt="Logo"
                    className="mw-100 brand-logo"
                  />
                </Link>
              </div>

              <div className="flex-center justify-content-between px-3 py-2 d-md-none d-flex">
                <div
                  className="position-relative hamburger-icon"
                  onClick={() => setOpenMobileMenu(false)}
                >
                  <MenuCloseSvg />
                </div>
              </div>
            </>
          )}
          Footer={() => (
            <div
              className={`d-flex justify-content-center ${
                collasped ? 'position-absolute bottom-0 end-0' : ''
              } d-none d-md-flex`}
            >
              <button
                className="btn btn--link rounded-pill px-2 menu-expand-icon"
                onClick={toggleSidebar}
              >
                <ExpandSvg />
              </button>
            </div>
          )}
        />

        <div className={`flex-grow-1 ${collasped ? 'main-content' : 'main-content-full'}`}>
          <Topbar setOpenMobileMenu={setOpenMobileMenu} />

          <div className="p-4">
            <Container fluid className="rounded-1 content content-mainsection pe-0 ps-1">
              <ProfileCompletionAlert />
              <Outlet />
            </Container>
          </div>
        </div>
      </div>
    </ProtectedAdminRoute>
  );
}
